import { useEffect, useState } from "react";
import { TrendingUp, User, File, FileCheck } from "lucide-react";
import { fetchDashboard } from "../api/endpoints/fetchDashboard";

export function AnalyticsPanel() {
  const [topForms, setTopForms] = useState([
    { name: "Total Managers in System", responses: 0, trend: 12, icon: User, color: "text-blue-600" },
    { name: "Total Files in System", responses: 0, trend: -5, icon: File, color: "text-purple-600" },
    { name: "Total Files Assigned to Managers", responses: 0, trend: 8, icon: FileCheck, color: "text-green-600" },
    { name: "Total Files with Error", responses: 0, trend: 8, icon: File, color: "text-red-600" },
  ]);

  useEffect(() => {
    async function load() {
      const res = await fetchDashboard();

      setTopForms(prev =>
        prev.map((item, i) => ({
          ...item,
          responses:
            i === 0 ? res.totalUsers :
              i === 1 ? res.totalFiles :
                i === 2 ? res.totalFilesAssigned : 0
        }))
      );
    }

    load();
  }, []);

  const maxResponses = 100;//Math.max(...topForms.map(f => f.responses || 1));

  const completionData = [
    { month: "Jul", value: 82, barHeight: 82 / 89 },
    { month: "Aug", value: 79, barHeight: 79 / 89 },
    { month: "Sep", value: 85, barHeight: 85 / 89 },
    { month: "Oct", value: 89, barHeight: 89 / 89 },
    { month: "Nov", value: 68, barHeight: 68 / 89 },
    { month: "Dec", value: 75, barHeight: 75 / 89 },
  ];

  const insights = [
    { text: "Files processing has the highest completion rate at 92%", type: "success" },
    { text: "Files processed errors found - dropped 5%", type: "warning" },
    { text: "Files assigned this month", type: "success" },
    { text: "Files completed this month 18%", type: "info" },
  ];

  return (
    <div className="p-6 space-y-6">

      {/* General Statistics (Enhanced UI) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900">General Statistics</h4>
          <span className="text-xs text-gray-500">Overview</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {topForms.map((form, idx) => {
            const Icon = form.icon;
            const cardBg = idx === 0 ? "from-blue-50" : idx === 1 ? "from-purple-50" : idx === 2 ? "from-green-50" : "from-red-50";
            return (
              <div
                key={idx}
                className={`rounded-xl p-4 border border-gray-200 bg-gradient-to-br ${cardBg} to-white`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-white/70 border border-gray-200 flex items-center justify-center">
                    <Icon className={`w-3 h-3 ${form.color}`} />
                  </div>
                  <p className="text-xs text-gray-500">{form.name}</p>
                </div>
                <div className="flex items-end justify-between gap-3">
                  <p className="text-xl font-medium text-gray-900 tracking-tight">{form.responses}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <TrendingUp className="w-3 h-3 text-green-600" />
                    <span>{Math.abs(form.trend)}%</span>
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full"
                    style={{ width: `${(form.responses / maxResponses) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trends Chart */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-normal text-gray-900">Overall Files added and processed trends</h3>
          <span className="text-xs text-gray-500">Last 6 months</span>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-end justify-between h-32 gap-2">
            {completionData.map((item, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-primary rounded-t hover:bg-primary-hover transition-all cursor-pointer"
                  style={{ height: `${item.barHeight * 100}px` }} title={`${item.value}%`}
                />
                <span className="text-xs text-gray-500">{item.month}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 text-xs">
          <TrendingUp className="w-4 h-4 text-green-600" />
          <span className="text-gray-700">
            <span className="text-green-600 font-medium">+21%</span> completion rate vs last period
          </span>
        </div>
      </div>

      {/* Insights */}
      <div>
        <h3 className="text-sm font-normal text-gray-900 mb-4">Key Insights</h3>
        <div className="space-y-2">
          {insights.map((insight, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-3 p-3 rounded-lg border ${insight.type === "success"
                ? "bg-green-50 border-green-200"
                : insight.type === "warning"
                  ? "bg-yellow-50 border-yellow-200"
                  : "bg-blue-50 border-blue-200"
                }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full mt-1.5 ${insight.type === "success"
                  ? "bg-green-500"
                  : insight.type === "warning"
                    ? "bg-yellow-500"
                    : "bg-blue-500"
                  }`}
              />
              <p className="text-xs text-gray-700 flex-1">{insight.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div>
        <h3 className="text-sm font-normal text-gray-900 mb-4">Quick Stats</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Avg. Response Time</p>
            <p className="text-lg font-normal text-gray-900">12.4m</p>
            <p className="text-xs text-green-600 mt-1">-2m from last month</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Total Forms</p>
            <p className="text-lg font-normal text-gray-900">24</p>
            <p className="text-xs text-gray-500 mt-1">3 added this month</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Active Users</p>
            <p className="text-lg font-normal text-gray-900">9</p>
            <p className="text-xs text-green-600 mt-1">+12 this week</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Completion Rate</p>
            <p className="text-lg font-normal text-gray-900">89%</p>
            <p className="text-xs text-green-600 mt-1">+4% this month</p>
          </div>
        </div>
      </div>

      {/* Export Button */}
      {/* <div className="pt-4 border-t border-gray-200">
        <button className="w-full text-sm text-gray-700 hover:text-gray-900 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
          Export Full Report
        </button>
      </div> */}
    </div>
  );
}
